import {
  Body,
  Button,
  Heading,
  Icon,
  useToast,
} from "@elaniintc/goes-design-system";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EmptyState } from "./EmptyState";
import { useHeaderButton } from "@/contexts/header-button-context";
import { useNavigate } from "react-router";
import { TagProcessComponent } from "./TagProcessBlock";
import { useGetTags } from "@/queries/tags/tags.api";
import { Skeleton } from "@/components/ui/skeleton";
import Pagination from "@/components/common/table/pagination";
import {
  useSuspensionState,
  type ProcessConfig,
} from "../hooks/useSuspensionState";
import {
  useGetSuspensionConfigs,
  useUpdateDeadlineSuspension,
} from "@/queries/deadline-suspensions/deadline-suspensions.api";
import dayjs from "dayjs";

export const SuspensionsDetail = () => {
  const [isEmpty, setIsEmpty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [page, setPage] = useState(1);
  const { mutateAsync: saveSuspensionConfigs } = useUpdateDeadlineSuspension();
  const { data: configs, isLoading: isLoadingConfigs } =
    useGetSuspensionConfigs(true);
  const {
    data: tagsData,
    isLoading,
    isFetching,
  } = useGetTags({
    page,
    per_page: 20,
    relationships: [
      "processes",
      "processes.deadlineSuspensionConfig",
      "globalDeadlineSuspensionConfig",
    ].join(","),
  });

  const { setComp } = useHeaderButton();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const {
    initialize,
    updateTagMaxDays,
    toggleGeneralConfig,
    updateProcessMaxDays,
    hasChanges,
    getChangesSummary,
    state,
  } = useSuspensionState();

  // Keep ref to latest state for stable callbacks
  const stateRef = useRef(state);
  stateRef.current = state;

  // Initialize state when tags data loads
  useEffect(() => {
    if (tagsData?.data) {
      if (tagsData.data.length === 0) {
        setIsEmpty(true);
      } else {
        setIsEmpty(false);
        initialize(tagsData.data);
      }
    }
  }, [tagsData, initialize]);

  // Filter current page tags from state
  const currentPageTags = useMemo(
    () => state.configs.filter((c) => c.type === "tag"),
    [state.configs],
  );

  const handleUpdateMaxDays = useCallback(
    (id: string, maxDays: number) => {
      const config = stateRef.current.configs.find((c) => c.id === id);
      if (!config) return;

      if (config.type === "tag") {
        updateTagMaxDays(id, maxDays);
      } else if (config.type === "process") {
        updateProcessMaxDays(config.tagId, id, maxDays);
      }
    },
    [updateTagMaxDays, updateProcessMaxDays],
  );

  // Refs for stable callbacks to avoid loop
  const handleSaveRef = useRef<() => void>(() => {});
  const handleCancelRef = useRef<() => void>(() => {});

  handleSaveRef.current = async () => {
    setIsSaving(true);
    try {
      const changes = getChangesSummary();
      await saveSuspensionConfigs(changes);
      showToast({
        title: "Cambios guardados",
        description: "Los plazos de suspensión han sido actualizados.",
        options: { type: "success" },
      });
      navigate("/portal");
    } catch (error) {
      console.error("Error saving:", error);
      showToast({
        title: "Error",
        description: "No se pudieron guardar los cambios.",
        options: { type: "error" },
      });
    } finally {
      setIsSaving(false);
    }
  };

  handleCancelRef.current = () => {
    navigate("/portal");
  };

  useEffect(() => {
    if (isEmpty) {
      setComp(null);
    } else {
      setComp(
        <>
          <Button
            variant="quiet"
            size="xs"
            type="button"
            onClick={() => handleCancelRef.current?.()}
            disabled={isSaving}
          >
            Cancelar
            <Icon type="far" name="times" />
          </Button>
          <Button
            variant="default"
            size="xs"
            type="button"
            onClick={() => handleSaveRef.current?.()}
            disabled={!hasChanges || isSaving}
          >
            Guardar cambios
            {isSaving ? (
              <Icon
                type="far"
                name="spinner-third"
                className="animate-spin text-current"
              />
            ) : (
              <Icon type="far" name="save" className="text-current" />
            )}
          </Button>
        </>,
      );
    }
    return () => {
      setComp(null);
    };
  }, [setComp, isEmpty, isSaving, hasChanges]);

  let tagsContent;
  if (isLoading || isFetching) {
    tagsContent = (
      <div
        className="w-full flex flex-col mt-2 gap-2 overflow-x-hidden"
        data-testid="suspensions-skeleton"
      >
        {[1, 2, 3, 4, 5, 6, 7].map((item) => (
          <Skeleton
            key={item}
            className="w-full gap-4 h-17 rounded-xl px-4 py-3 "
          />
        ))}
      </div>
    );
  } else if (isEmpty) {
    tagsContent = <EmptyState onViewLabels={() => navigate("/procesos")} />;
  } else {
    tagsContent = (
      <div>
        {tagsData?.data.map((tag) => {
          const tagConfig = currentPageTags.find((c) => c.id === tag.id);
          if (!tagConfig) return null;
          if (tagConfig.type !== "tag") return null;

          const processConfigs = state.configs.filter(
            (c): c is ProcessConfig =>
              c.type === "process" && c.tagId === tag.id,
          );

          return (
            <TagProcessComponent
              key={tag.id}
              tag={tag}
              config={tagConfig}
              onUpdateMaxDays={handleUpdateMaxDays}
              onToggleGeneralConfig={toggleGeneralConfig}
              processesConfig={processConfigs}
            />
          );
        })}
        <div className="mt-8">
          {tagsData?.meta && (
            <Pagination
              currentPage={tagsData.meta.current_page}
              totalItems={tagsData.meta.total}
              hasNext={tagsData.meta.current_page < tagsData.meta.last_page}
              hasPrevious={tagsData.meta.current_page > 1}
              onNext={() =>
                setPage(Math.min(page + 1, tagsData.meta.last_page))
              }
              onPageChange={setPage}
              onPrevious={() => setPage(Math.max(page - 1, 1))}
              pageSize={20}
              totalPages={tagsData.meta.last_page}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="py-3 laptop:py-6 desktop:py-8 flex flex-col gap-8 h-full">
      <div>
        <Heading variant="h5">Plazos de suspensión</Heading>
        <Body className="leading-3xl">
          Configura el tiempo máximo de las suspensiones por cada tipo de
          trámite, o por trámite individual.
        </Body>

        {isLoadingConfigs ? (
          <Skeleton className="w-1/2 gap-4 h-1 rounded-lg px-4 py-3 mt-1" />
        ) : (
          configs?.last_bulk_operation?.performed_at && (
            <Body size="sm" className="leading-2xl">
              Última actualización:{" "}
              {dayjs(configs.last_bulk_operation.performed_at).format(
                "MMM DD, YYYY | HH:mm A",
              )}{" "}
              {configs.last_bulk_operation.user?.full_name && (
                <>
                  por{" "}
                  <span className="text-action">
                    {configs.last_bulk_operation.user?.full_name}
                  </span>
                </>
              )}
            </Body>
          )
        )}
      </div>
      {tagsContent}
    </div>
  );
};
